
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import OrganizationTile from "@/components/OrganizationTile";
import Header from "@/components/Header";
import { Button } from "@/components/ui/button";
import { Menu, Plus, X } from "lucide-react";
import type { Organization } from "@/integrations/supabase/client";
import { useIsMobile } from "@/hooks/use-mobile";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useToast } from "@/components/ui/use-toast";
import { useHeaderData } from "@/hooks/use-header-data";

const Organizations = () => {
  const navigate = useNavigate();
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const [organizations, setOrganizations] = useState<Organization[]>([]);
  const [loading, setLoading] = useState(true);
  const [currentUserId, setCurrentUserId] = useState<string>("");
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [currentUser, setCurrentUser] = useState<any>(null);
  const isMobile = useIsMobile();

  console.log("🔍 DEBUG: Rendering Organizations page");

  // Query to get the default organization
  const { data: defaultOrgId, refetch: refetchDefaultOrg } = useQuery({
    queryKey: ["default-organization"],
    queryFn: async () => {
      if (!currentUserId) {
        console.log("🔍 DEBUG: No current user ID, skipping default org fetch");
        return null;
      }
      
      console.log("🔍 DEBUG: Fetching default organization for user:", currentUserId);
      
      try {
        const { data, error } = await supabase
          .from("organization_members")
          .select("organization_id, is_default")
          .eq("user_id", currentUserId)
          .eq("is_default", true)
          .maybeSingle();
        
        if (error) {
          console.error("🔴 ERROR: Error fetching default organization:", error);
          return null;
        }
        
        if (!data) {
          console.log("🔍 DEBUG: No default organization found");
          return null;
        }
        
        console.log("🔍 DEBUG: Found default organization:", data.organization_id);
        return data.organization_id;
      } catch (error) {
        console.error("🔴 ERROR: Exception in default organization fetch:", error);
        return null;
      }
    },
    enabled: !!currentUserId,
    staleTime: 0, // Add this to ensure fresh data after changes
  });

  useEffect(() => {
    const checkAuth = async () => {
      try {
        console.log("🔍 DEBUG: Checking auth session");
        const { data: { session }, error } = await supabase.auth.getSession();
        
        if (error) {
          console.error("🔴 ERROR: Error checking auth session:", error);
          navigate("/login");
          return;
        }
        
        if (!session) {
          console.log("🔍 DEBUG: No session found, navigating to login");
          navigate("/login");
          return;
        }
        
        console.log("🔍 DEBUG: User authenticated. Setting currentUserId:", session.user.id);
        setCurrentUserId(session.user.id);
        
        // Get user data for the UserProfile component
        const { data: userData, error: userError } = await supabase.auth.getUser();
        if (userError) {
          console.error("🔴 ERROR: Error fetching user data:", userError);
        } else {
          console.log("🔍 DEBUG: User data fetched successfully:", userData.user);
          setCurrentUser(userData.user);
        }
        
        fetchOrganizations(session.user.id);
      } catch (error) {
        console.error("🔴 ERROR: Exception in checkAuth:", error);
        navigate("/login");
      }
    };
    
    checkAuth();
  }, [navigate]);

  const fetchOrganizations = async (userId: string) => {
    try {
      setLoading(true);
      console.log("🔍 DEBUG: Fetching organizations for user:", userId);
      
      // First, get the organization IDs this user is a member of
      const { data: memberData, error: memberError } = await supabase
        .from("organization_members")
        .select("organization_id, is_default")
        .eq("user_id", userId);
        
      if (memberError) {
        console.error("🔴 ERROR: Error fetching organization memberships:", memberError);
        throw memberError;
      }
      
      if (!memberData || memberData.length === 0) {
        console.log("🔍 DEBUG: No organizations found for user");
        setOrganizations([]);
        setLoading(false);
        return;
      }
      
      console.log("🔍 DEBUG: Found membership data:", memberData);
      console.log("🔍 DEBUG: Default organizations in memberships:", 
        memberData.filter(m => m.is_default).map(m => m.organization_id));
      
      // Extract the organization IDs
      const organizationIds = memberData.map(member => member.organization_id);
      
      // Then fetch the actual organizations
      const { data, error } = await supabase
        .from("organizations")
        .select("*")
        .in("id", organizationIds);

      if (error) {
        console.error("🔴 ERROR: Error fetching organizations:", error);
        throw error;
      }
      
      console.log("🔍 DEBUG: Fetched organizations:", data);
      setOrganizations(data || []);
    } catch (error) {
      console.error("🔴 ERROR: Error in fetchOrganizations:", error);
      toast({
        title: "Error",
        description: "Failed to load organizations",
        variant: "destructive"
      });
    } finally {
      setLoading(false);
    }
  };

  const handleOrganizationSelect = (orgId: string) => {
    console.log("🔍 DEBUG: Organization selected. Navigating to:", `/app/${orgId}/dashboard`);
    navigate(`/app/${orgId}/dashboard`);
  };

  const handleCreateOrganization = () => {
    navigate("/create-organization");
  };

  const handleMarkDefault = async (orgId: string) => {
    try {
      console.log("🔍 DEBUG: handleMarkDefault called in Organizations page for orgId:", orgId);
      
      // Check if user is authenticated
      if (!currentUserId) {
        console.error("🔴 ERROR: No current user ID found");
        toast({
          title: "Authentication error",
          description: "You need to be logged in to perform this action",
          variant: "destructive"
        });
        return;
      }

      console.log("🔍 DEBUG: Setting default organization:", orgId, "for user:", currentUserId);
      
      // First, update all memberships to set is_default to false
      const { error: resetError } = await supabase
        .from("organization_members")
        .update({ is_default: false })
        .eq("user_id", currentUserId);
        
      if (resetError) {
        console.error("🔴 ERROR: Error resetting previous defaults:", resetError);
        throw resetError;
      }
      
      // Then set the selected organization as default
      const { error: updateError } = await supabase
        .from("organization_members")
        .update({ is_default: true })
        .eq("organization_id", orgId)
        .eq("user_id", currentUserId);
      
      if (updateError) {
        console.error("🔴 ERROR: Error setting default organization:", updateError);
        throw updateError;
      }
      
      console.log("✅ DEBUG: Default organization set successfully");
      
      // Refetch default org to update UI immediately
      await refetchDefaultOrg();
      
      // Show success notification
      toast({
        title: "Default organization set",
        description: `The selected organization is now your default.`,
      });
      
      // Invalidate related queries to ensure data consistency
      queryClient.invalidateQueries({ queryKey: ["default-organization"] });
      queryClient.invalidateQueries({ queryKey: ["default-organization-redirect"] });
      queryClient.invalidateQueries({ queryKey: ["default-organization-nav"] });
      
      // Refetch organizations to update UI
      await fetchOrganizations(currentUserId);
    } catch (error: any) {
      console.error("🔴 ERROR: Error marking organization as default:", error);
      toast({
        title: "Error",
        description: error.message || "Failed to set default organization",
        variant: "destructive",
      });
    }
  };

  // If there's only one organization and no default, set it as default automatically
  useEffect(() => {
    const autoSetDefaultOrg = async () => {
      // Only proceed if we have organizations, a user ID, and we've confirmed there's no default org yet
      if (organizations.length === 1 && currentUserId && defaultOrgId === null) {
        console.log("🔍 DEBUG: Auto-setting default organization", organizations[0].id);
        try {
          await handleMarkDefault(organizations[0].id);
        } catch (error) {
          console.error("🔴 ERROR: Error auto-setting default organization:", error);
        }
      }
    };
    
    // Only run this effect if we have all the required data
    if (organizations.length > 0 && currentUserId && defaultOrgId !== undefined) {
      autoSetDefaultOrg();
    }
  }, [organizations, currentUserId, defaultOrgId]);

  const toggleSidebar = () => {
    setSidebarOpen(!sidebarOpen);
  };

  // Mobile navigation menu items
  const navItems = [
    { title: "Organizations", onClick: () => navigate("/app/organizations") },
    { title: "Create Organization", onClick: handleCreateOrganization },
    { title: "Settings", onClick: () => navigate("/settings") },
  ];

  console.log("🔍 DEBUG: Current defaultOrgId:", defaultOrgId);
  console.log("🔍 DEBUG: Organizations:", organizations);

  return (
    <div className="min-h-screen bg-background">
      {/* Sidebar overlay for mobile */}
      {sidebarOpen && isMobile && (
        <div 
          className="fixed inset-0 bg-black/50 z-20"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Mobile sidebar menu */}
      {isMobile && sidebarOpen && (
        <div className="fixed top-0 left-0 h-screen w-64 bg-white z-30 shadow-lg overflow-auto">
          <div className="flex justify-between items-center p-4 border-b">
            <div className="flex items-center gap-2">
              <div className="w-8 h-8 rounded-md bg-purple-600 flex items-center justify-center">
                <span className="font-bold text-lg text-white">E</span>
              </div>
              <span className="font-semibold">Expense</span>
            </div>
            <Button variant="ghost" size="icon" onClick={toggleSidebar}>
              <X className="h-5 w-5" />
            </Button>
          </div>
          <div className="p-4 space-y-2">
            {navItems.map((item) => (
              <Button 
                key={item.title} 
                variant="ghost" 
                className="w-full justify-start text-left"
                onClick={() => {
                  item.onClick();
                  setSidebarOpen(false);
                }}
              >
                {item.title}
              </Button>
            ))}
          </div>
        </div>
      )}
      
      <Header 
        user={currentUser}
        organization={null}
        organizations={organizations}
      />
      
      <main className="container mx-auto px-4 py-6 md:py-8">
        <div className="max-w-5xl mx-auto">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6 md:mb-8">
            <h1 className="text-2xl md:text-3xl font-bold">Organizations</h1>
            <Button onClick={handleCreateOrganization} className="w-full sm:w-auto">
              <Plus className="h-4 w-4 mr-2" />
              Create Organization
            </Button>
          </div>
          
          {loading ? (
            <div className="py-12 flex justify-center">
              <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-primary"></div>
            </div>
          ) : organizations.length === 0 ? (
            <div className="bg-muted/40 rounded-lg p-6 md:p-8 text-center">
              <h3 className="text-xl font-semibold mb-2">No organizations found</h3>
              <p className="text-muted-foreground mb-6">You haven't created any organizations yet.</p>
              <Button onClick={handleCreateOrganization} className="w-full sm:w-auto">
                <Plus className="h-4 w-4 mr-2" />
                Create your first organization
              </Button>
            </div>
          ) : (
            <div className="grid gap-4">
              {organizations.map((org) => {
                const isDefault = org.id === defaultOrgId;
                console.log("🔍 DEBUG: Rendering OrganizationTile for:", org.name, "id:", org.id, "isDefault:", isDefault);
                return (
                  <OrganizationTile
                    key={org.id}
                    organization={org}
                    onGoToOrganization={() => handleOrganizationSelect(org.id)}
                    onEdit={() => fetchOrganizations(currentUserId)}
                    onDelete={() => fetchOrganizations(currentUserId)}
                    onToggleLock={() => fetchOrganizations(currentUserId)}
                    onMarkDefault={() => handleMarkDefault(org.id)}
                    isDefault={isDefault}
                  />
                );
              })}
            </div>
          )}
        </div>
      </main>
    </div>
  );
};

export default Organizations;
